'use client';

import Link from 'next/link';
import type { MouseEventHandler } from 'react';
import { useAuth } from 'react-oidc-context';

import { AvatarScratch, Home, SearchMenu, Simple, Versus } from '@zoomit/icons';
import { cssVar, useOverlayStateHandler } from '@zoomit/primitives';
import { useHistoryState, useNotificationCount } from '@zoomit/web-kit';
import { theme } from '@zoomit/zoomit-ds';

import CategorySheet from './CategorySheet';
import { OthersSheet } from './OthersSheet';
import ProfileSheet from './ProfileSheet';

export const BottomNavbar = () => {
  const { isAuthenticated, signinRedirect } = useAuth();
  const otherSheetOverlay = useOverlayStateHandler();

  const categoryModalStates = useHistoryState({
    id: 'category-modal',
    initialState: false,
  });

  const profileSheetOverlay = useOverlayStateHandler();

  const { data: notificationsCount } = useNotificationCount();

  return (
    <>
      <div className="bg-background2 h-14 w-full md:hidden" />
      <div className="bg-background2 border-borderPrimary fixed right-0 bottom-0 left-0 z-10 grid w-full grid-cols-5 items-center justify-items-center border-t md:hidden">
        <NavbarItem href="/" icon={<Home size="md" fill={cssVar(theme.text)} />} title="خانه" />
        <NavbarItem
          href="/category"
          icon={<Simple size="md" fill={cssVar(theme.text)} />}
          title="دسته بندی"
          onClick={categoryModalStates.open}
          type="button"
        />
        <NavbarItem
          href="/compare/list"
          icon={<Versus size="md" stroke="none" fill={cssVar(theme.text)} />}
          title="مقایسه"
        />
        <NavbarItem
          icon={
            <span>
              {Boolean(notificationsCount?.data) && (
                <div className="bg-error absolute top-0.5 right-0 rounded-full px-1 py-0.5 text-white">
                  {notificationsCount?.data}
                </div>
              )}
              <AvatarScratch size="md" fill={cssVar(theme.text)} />
            </span>
          }
          title="زوبین من"
          type="button"
          onClick={(e) => {
            if (isAuthenticated) {
              profileSheetOverlay.handleOpenModal();
              return;
            }
            signinRedirect({ url_state: window.location.href });
          }}
        />

        <NavbarItem
          onClick={otherSheetOverlay.handleOpenModal}
          type="button"
          icon={<SearchMenu size="md" fill={cssVar(theme.text)} strokeWidth={0.5} />}
          title="سایر"
        />
      </div>
      <OthersSheet isOpen={otherSheetOverlay.isOpen} onClose={otherSheetOverlay.handleCloseModal} />
      <CategorySheet isOpen={categoryModalStates.isOpen} onClose={categoryModalStates.close} />
      <ProfileSheet onClose={profileSheetOverlay.handleCloseModal} isOpen={profileSheetOverlay.isOpen} />
    </>
  );
};

type NavBarItemProps = {
  title: string;
  icon: React.ReactNode;
  href?: string;
  onClick?: MouseEventHandler;
  type?: 'button' | 'link';
};

function NavbarItem(props: NavBarItemProps) {
  const { title, icon, href, onClick, type = 'link' } = props;
  const Wrapper = type === 'button' ? 'div' : Link;

  return (
    <Wrapper
      href={href ?? ''}
      onClick={onClick}
      className="relative flex cursor-pointer flex-col items-center justify-center gap-2 pt-2.5 pb-1.5"
      prefetch
    >
      {icon}
      <span>{title}</span>
    </Wrapper>
  );
}
